import {
  Navbar,
  NavbarContent,
  NavbarItem,
  Avatar,
  Button,
  Listbox,
  ListboxItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Link
} from '@heroui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSun,
  faMoon,
  faGear,
  faRightFromBracket,
  faBars
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '@heroui/use-theme';
import { useAppSelector } from 'store/hooks';
import { selectUserName } from 'store/user/selectors';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { clearTokenInfo } from 'src/utils/authUtils';
import { clearImpersonationStorage } from '../store/user/userSlice';
import GlobalSearch from './GlobalSearch';
import { UserSwitcher } from './admin/UserSwitcher';

import logo from '../assets/logo.svg';

interface HeaderProps {
  onMobileMenuToggle?: () => void;
  showSignUpButton?: boolean;
}

const Header: React.FC<HeaderProps> = ({ onMobileMenuToggle, showSignUpButton = false }) => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme('dark');
  const userName = useAppSelector(selectUserName);
  const user = useAppSelector((state) => state.auth.user);
  const userPic = user?.discordAvatar;

  const { logout, loginWithRedirect, isAuthenticated } = useAuth0();

  const handleLogout = () => {
    clearTokenInfo();
    clearImpersonationStorage();
    logout({ logoutParams: { returnTo: window.location.origin + '/logout' } });
  };

  const handleSignUp = () => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup'
      },
      appState: { returnTo: window.location.pathname }
    });
  };

  return (
    <Navbar
      isBordered
      height="3rem"
      className={
        showSignUpButton
          ? 'py-0 bg-slate-600 dark:bg-gray-950 [&>header]:flex-nowrap'
          : 'py-0 bg-slate-100 dark:bg-gray-950 [&>header]:flex-nowrap'
      }
      maxWidth="full">
      <NavbarContent justify="start" className="flex-1 gap-2 px-2">
        {showSignUpButton && (
          <div className="my-1">
            <Link href="https://thedmsark.com" className="flex items-center">
              <img src={logo} alt="Logo" className="h-10 w-auto" />
              <span className="text-sm font-bold text-default-100 dark:text-default-900 ml-2">
                The DM's Ark
              </span>
            </Link>
          </div>
        )}

        {!showSignUpButton && (
          <>
            <NavbarItem className="lg:hidden">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onPress={onMobileMenuToggle}
                className="text-gray-600 dark:text-gray-300">
                <FontAwesomeIcon icon={faBars} />
              </Button>
            </NavbarItem>
            <NavbarItem className="flex-1 lg:flex-none lg:w-full lg:mx-auto">
              <div className="block lg:hidden">
                <GlobalSearch variant="button" />
              </div>
              <div className="hidden lg:block">
                <GlobalSearch variant="input" />
              </div>
            </NavbarItem>
          </>
        )}
      </NavbarContent>

      <NavbarContent justify="end">
        {!showSignUpButton && user?.isAdmin && (
          <NavbarItem className="hidden md:flex">
            <UserSwitcher />
          </NavbarItem>
        )}
        <NavbarItem>
          <Button
            isIconOnly
            variant="light"
            size="sm"
            onPress={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
            <FontAwesomeIcon
              icon={theme === 'light' ? faMoon : faSun}
              className="text-gray-900 dark:text-gray-300"
            />
          </Button>
        </NavbarItem>
        {showSignUpButton && !isAuthenticated ? (
          <NavbarItem>
            <Button color="primary" size="sm" onPress={handleSignUp} className="font-medium">
              Sign Up
            </Button>
          </NavbarItem>
        ) : (
          <>
            <NavbarItem>
              <span className="text-xs text-gray-600 dark:text-gray-300 hidden sm:inline">
                {userName}
              </span>
            </NavbarItem>
            <NavbarItem>
              <Popover placement="bottom-end">
                <PopoverTrigger>
                  <Avatar
                    isBordered
                    as="button"
                    name={userName}
                    className="transition-transform"
                    color="default"
                    size="sm"
                    src={userPic}
                    type="button"
                  />
                </PopoverTrigger>
                <PopoverContent className="p-1">
                  <Listbox aria-label="User menu" variant="flat" color="default">
                    <ListboxItem
                      key="settings"
                      startContent={<FontAwesomeIcon icon={faGear} />}
                      onPress={() => navigate('/settings')}>
                      Settings
                    </ListboxItem>
                    <ListboxItem
                      key="logout"
                      className="text-danger"
                      color="danger"
                      startContent={<FontAwesomeIcon icon={faRightFromBracket} />}
                      onPress={handleLogout}>
                      Logout
                    </ListboxItem>
                  </Listbox>
                </PopoverContent>
              </Popover>
            </NavbarItem>
          </>
        )}
      </NavbarContent>
    </Navbar>
  );
};

export default Header;
